import React, { useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../i18n";

export default function Footer({ isHomePage = false }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isHome = location.pathname === "/" || location.pathname === "/home";
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const languageDropdownRef = useRef(null);

  // Dil seçenekleri
  const languages = [
    { code: "tr", name: "Türkçe" },
    { code: "en", name: "English" },
    { code: "fr", name: "Français" },
    { code: "bg", name: "Български" },
    { code: "el", name: "Ελληνικά" },
    { code: "es", name: "Español" },
    { code: "it", name: "Italiano" },
    { code: "ru", name: "Русский" },
  ];

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) || languages[0];

  const footerLinks = {
    pages: [
      { href: "/", label: t("HomePage") },
      { href: "/discover", label: t("Discover") },
      { href: "/leave-mark", label: t("LeaveMark") },
      { href: "/download", label: t("InstallNow") }
    ],
    privacy: [
      { href: "/policies/privacy-policy", label: t("PrivacyPolicy") },
      { href: "/policies/terms-of-use", label: t("TermsOfUse") },
      { href: "/policies/data-security", label: t("DataSecurity") },
      { href: "/policies/cookie-policy", label: t("CookiePolicy") },
      { href: "/policies/account-security", label: t("AccountSecurity") },
      { href: "/policies/legal-disclaimer-and-statement", label: t("LegalConsentAndDisclosureText") },
      { href: "/policies/child-safety-policy", label: t("ChildSafetyPolicy") }
    ],
  };

  const socialLinks = [
    {
      href: "https://www.facebook.com",
      icon: "/assets/facebook.svg",
      alt: "Facebook",
    },
    {
      href: "https://www.twitter.com",
      icon: "/assets/twitter.svg",
      alt: "Twitter",
    },
    {
      href: "https://www.instagram.com",
      icon: "/assets/instagram.svg",
      alt: "Instagram",
    },
    {
      href: "https://www.linkedin.com",
      icon: "/assets/linkedin.svg",
      alt: "LinkedIn",
    },
  ];

  return (
    <footer className="py-8 md:py-12 lg:py-16 px-4 md:px-8 lg:px-16">
      <div className="container mx-auto max-w-[1245px]">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 md:gap-12 lg:gap-16">
          {/* Logo and Contact Info */}
          <div className="flex flex-col items-center space-y-6 lg:col-span-3">
            <img
              src="/assets/qriendLogoGri.png"
              alt="Qriend Logo"
              className="h-12 md:h-14 lg:h-[70px] w-auto lg:mr-36"
              loading="lazy"
            />
            <p className="text-base md:text-base text-[#797979] text-center md:text-left max-w-80">
             {t("QriendDescFooter")}
            </p>
          </div>

          {/* Pages Links */}
          <div className="lg:col-span-2  mx-4">
            <div className="flex flex-col items-center md:items-start space-y-4 ">
              <h6 className="text-xl font-bold text-[#242331] text-center md:text-left">
                {t("Pages")}
              </h6>
              <hr className="w-full border-gray-200 " />
              <nav className="grid grid-cols-2 md:grid-cols-1 gap-x-4 gap-y-3 md:gap-0  w-full">
                {footerLinks.pages.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="text-sm md:text-base text-[#797979] hover:text-[#E94057] transition-colors duration-300 py-1 text-left w-full"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>
          </div>

          {/* Privacy Links */}
          <div className="lg:col-span-3 mx-4">
            <div className="flex flex-col items-center md:items-start space-y-4">
              <h6 className="text-xl font-bold text-[#242331] text-center md:text-left">
               {t("PrivacyAndSecurity")}
              </h6>
              <hr className="w-full border-gray-200 " />
              <nav className="grid grid-cols-2 md:grid-cols-1 gap-x-4 gap-y-3 md:gap-0   w-full">
                {footerLinks.privacy.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="text-sm md:text-base text-[#797979] hover:text-[#E94057] transition-colors duration-300 py-1 text-left w-full"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>
          </div>

          {/* Download Card */}
          <div className="lg:col-span-4">
            <div
              className={`p-6 md:p-8 rounded-3xl ${
                isHomePage ? "bg-white" : "bg-[#F9F9F9]"
              } shadow-lg w-full sm:max-w-[443px] h-auto min-h-[280px] sm:h-[300px] mx-auto lg:ml-auto flex flex-col justify-between`}
            >
              <div className="space-y-4 text-center sm:text-left">
                <p className="text-lg md:text-xl text-[#E94057] font-bold">
                 {t("InstallNow")}
                </p>
                <h3 className="text-xl md:text-2xl text-gray-900 font-bold">
                  {t("SuitableForAllDevices")}
                </h3>
                <p className="text-sm md:text-base text-gray-500">
                  {t("DownloadNowFooter")}
                </p>
              </div>

              {/* Mobile Download Buttons */}
              <div className="flex sm:hidden mt-4 justify-center">
                <div className="flex flex-col items-center gap-1">
                  <a
                    href="https://apps.apple.com/app/qriend"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-[120px] h-[50px]"
                  >
                    <img
                      src="/assets/appStore.svg"
                      alt="App Store'dan İndir"
                      className="w-full h-auto transform hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.qriend"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-[120px] h-[50px]"
                  >
                    <img
                      src="/assets/googlePlay.svg"
                      alt="Google Play'den İndir"
                      className="w-full h-auto transform hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </a>
                </div>
              </div>

              {/* Desktop/Tablet Download Buttons */}
              <div className="hidden sm:flex flex-row items-center justify-center gap-3 pb-10">
                <a
                  href="https://apps.apple.com/app/qriend"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center h-[50px] w-[167px] transform hover:scale-105 transition-transform duration-300"
                >
                  <img
                    src="/assets/appStore.svg"
                    alt="App Store'dan İndir"
                    className="w-full h-auto"
                    loading="lazy"
                  />
                </a>

                <a
                  href="https://play.google.com/store/apps/details?id=com.qriend"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center h-[50px] w-[167px] transform hover:scale-105 transition-transform duration-300"
                >
                  <img
                    src="/assets/googlePlay.svg"
                    alt="Google Play'den İndir"
                    className="w-full h-auto"
                    loading="lazy"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright and Social Links */}
        <div className="mt-12 md:mt-16 pt-6 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 md:gap-4">
            <p className="text-sm md:text-base text-gray-500 text-center md:text-left">
              © Copyright 2025. All rights reserved.
            </p>
            <div className="flex items-center   ">
              {socialLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#E94057]/10 transition-colors duration-300"
                  aria-label={`Bizi ${link.alt}'da takip edin`}
                >
                  <img
                    src={link.icon}
                    alt={link.alt}
                    className="w-6 h-6 opacity-70 hover:opacity-100 transition-opacity duration-300"
                    loading="lazy"
                  />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
